import { Divider, Tag } from "antd";
import {
  Activity,
  AlertTriangle,
  Ban,
  Briefcase,
  Building,
  Building2,
  Calendar,
  CheckCircle2,
  Database,
  ExternalLink,
  FileText,
  Globe,
  History,
  Landmark,
  MapPin,
  Shield,
  Tag as TagIcon,
} from "lucide-react";

import type { BusinessEntityDetailVO } from "@/api/entities/type";
import { SectionTitle } from "@/components/DetailBlocks";
import { useDict } from "@/hooks/useDict";

import { isAbnormalBusinessStatus } from "../constants";
import { formatBusinessTerm, formatDateTime, formatHistoryNames } from "../utils";
import { DetailInfoRow } from "./detailParts";

/** 工商信息 Tab：基本信息、经营状态、异常信息、数据来源 */
export const BasicInfoTab = ({ detail }: { detail: BusinessEntityDetailVO | null }) => {
  const { getDictLabel } = useDict();
  /** 是否展示异常信息区块 */
  const showAbnormal = detail?.newStatus && isAbnormalBusinessStatus(detail.newStatus);
  /** 经营状态 Tag 文案 */
  const statusLabel = getDictLabel("businessStatus", detail?.newStatus);

  return (
    <div className="space-y-5">
      <div>
        <SectionTitle icon={FileText} title="基本信息" />
        <div className="grid grid-cols-2 gap-x-6 gap-y-2.5">
          <DetailInfoRow icon={Landmark} label="企业名称" value={detail?.name} />
          {detail?.formatName && detail.formatName !== detail.name && (
            <DetailInfoRow icon={TagIcon} label="标准名称" value={detail.formatName} />
          )}
          <DetailInfoRow icon={FileText} label="信用代码" value={detail?.creditNo} mono />
          {detail?.regNo && (
            <DetailInfoRow icon={FileText} label="注册号" value={detail.regNo} mono />
          )}
          {detail?.orgNo && (
            <DetailInfoRow icon={FileText} label="组织机构号" value={detail.orgNo} mono />
          )}
          <DetailInfoRow icon={Building2} label="企业类型" value={detail?.econKind} />
          <DetailInfoRow
            icon={Building2}
            label="企业分类"
            value={getDictLabel("businessEntityCategoryNew", detail?.categoryNew)}
          />
          <DetailInfoRow
            icon={Globe}
            label="企业大类"
            value={getDictLabel("businessEntityTypeNew", detail?.typeNew)}
          />
          {detail?.domain && (
            <DetailInfoRow icon={Briefcase} label="行业分类" value={detail.domain} />
          )}
          <DetailInfoRow icon={Shield} label="注册资本" value={detail?.registCapi} />
          {detail?.actualCapi && (
            <DetailInfoRow icon={Shield} label="实缴资本" value={detail.actualCapi} />
          )}
          {detail?.currencyUnit && (
            <DetailInfoRow icon={Shield} label="货币单位" value={detail.currencyUnit} />
          )}
          {detail?.scope && (
            <DetailInfoRow icon={FileText} label="经营范围" value={detail.scope} full />
          )}
          <DetailInfoRow icon={MapPin} label="通信地址" value={detail?.address} />
          {detail?.districtCode && (
            <DetailInfoRow icon={MapPin} label="地区代码" value={detail.districtCode} mono />
          )}
          {detail?.fenname && (
            <DetailInfoRow icon={Globe} label="英文名称" value={detail.fenname} />
          )}
          <DetailInfoRow icon={Building} label="所属工商局" value={detail?.belongOrg} />
        </div>
      </div>
      <Divider className="my-0" />
      <div>
        <SectionTitle icon={Activity} title="经营状态" />
        <div className="grid grid-cols-2 gap-x-6 gap-y-2.5">
          <div className="flex items-center gap-1.5 text-sm min-w-0">
            <Activity className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            <span className="text-slate-500 w-24 shrink-0">经营状态</span>
            {detail?.newStatus ? (
              <Tag bordered className="text-[10px] m-0">
                {statusLabel}
              </Tag>
            ) : (
              "—"
            )}
          </div>
          <DetailInfoRow icon={Calendar} label="成立日期" value={detail?.startDate} />
          {detail?.checkDate && (
            <DetailInfoRow icon={CheckCircle2} label="核准日期" value={detail.checkDate} />
          )}
          <DetailInfoRow
            icon={Calendar}
            label="营业期限"
            value={formatBusinessTerm(detail?.termStart, detail?.termEnd)}
          />
          {!!detail?.tags?.length && (
            <div className="flex items-center gap-1.5 text-sm min-w-0 col-span-2">
              <TagIcon className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              <span className="text-slate-500 w-24 shrink-0">企业标签</span>
              <div className="flex flex-wrap gap-1">
                {detail.tags.map((tagCode) => (
                  <Tag key={tagCode} color="orange" className="text-[10px] m-0">
                    {getDictLabel("businessEntityTag", tagCode)}
                  </Tag>
                ))}
              </div>
            </div>
          )}
          {!!detail?.historyNames?.length && (
            <DetailInfoRow
              icon={History}
              label="历史名称"
              value={formatHistoryNames(detail.historyNames)}
              full
            />
          )}
        </div>
      </div>
      {showAbnormal && (
        <>
          <Divider className="my-0" />
          <div>
            <SectionTitle icon={AlertTriangle} title="异常信息" />
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg space-y-1.5 text-sm">
              <div className="flex items-center gap-2 text-red-700">
                <Ban className="w-4 h-4 shrink-0" />
                <span>当前经营状态：{statusLabel}</span>
              </div>
              {detail?.revokeDate && (
                <p className="text-red-600 pl-6 m-0">吊销日期：{detail.revokeDate}</p>
              )}
              {detail?.revokeReason && (
                <p className="text-red-600 pl-6 m-0">吊销原因：{detail.revokeReason}</p>
              )}
              {detail?.endDate && (
                <p className="text-red-600 pl-6 m-0">注销日期：{detail.endDate}</p>
              )}
              {detail?.logoutReason && (
                <p className="text-red-600 pl-6 m-0">注销原因：{detail.logoutReason}</p>
              )}
            </div>
          </div>
        </>
      )}
      <Divider className="my-0" />
      <div>
        <SectionTitle icon={Database} title="数据来源" />
        <div className="grid grid-cols-2 gap-x-6 gap-y-2">
          <DetailInfoRow
            icon={Database}
            label="来源"
            value={getDictLabel("businessEntityDataSource", detail?.dataSource)}
          />
          {detail?.lastSyncAt && (
            <DetailInfoRow
              icon={Calendar}
              label="最后同步"
              value={formatDateTime(detail.lastSyncAt)}
            />
          )}
          {detail?.syncProvider && (
            <DetailInfoRow
              icon={ExternalLink}
              label="同步渠道"
              value={getDictLabel("businessEntitySyncProvider", detail.syncProvider)}
            />
          )}
          <DetailInfoRow
            icon={Calendar}
            label="创建时间"
            value={formatDateTime(detail?.createAt)}
          />
          <DetailInfoRow
            icon={Calendar}
            label="更新时间"
            value={formatDateTime(detail?.updateAt)}
          />
        </div>
      </div>
    </div>
  );
};
